/**
 * Submission Triage — Customize columns drawer (OR-979).
 *
 * The drawer itself is shared with the Policies list
 * (`components/customize-columns-drawer.tsx`); this wrapper feeds it the
 * Triage column set for the active view. Submission ID is the row
 * identity — always visible, not toggleable and not draggable.
 */
import { useMemo } from "react";
import { type CustomizeApply, CustomizeColumnsDrawer } from "../../components/customize-columns-drawer";
import { GROUP_LABELS, GROUP_ORDER, type TriageView, allColumnIds, columnsForView, defaultVisibleIds } from "./triage-columns";

interface CustomizeDrawerProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    view: TriageView;
    /** Currently committed visible column ids. */
    value: string[];
    /** Currently committed column order ids. */
    order: string[];
    /** Commit a new visible set + order. */
    onApply: (next: CustomizeApply) => void;
}

const LOCKED = ["submission_id"];

export function CustomizeDrawer({ isOpen, onOpenChange, view, value, order, onApply }: CustomizeDrawerProps) {
    const columns = useMemo(() => columnsForView(view), [view]);
    const defaults = useMemo(() => defaultVisibleIds(view), [view]);
    const natural = useMemo(() => allColumnIds(view), [view]);
    return (
        <CustomizeColumnsDrawer
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            columns={columns}
            groupOrder={GROUP_ORDER}
            groupLabels={GROUP_LABELS}
            value={value}
            order={order}
            defaultVisibleIds={defaults}
            defaultOrder={natural}
            lockedIds={LOCKED}
            autotestPrefix="triage"
            onApply={onApply}
        />
    );
}
